import React, { useEffect, useState, useCallback } from "react";
import { apiClient } from "../lib/api";
import StockCard from "../components/StockCard";
import ScoreBreakdownModal from "../components/ScoreBreakdownModal";
import InsightModal from "../components/InsightModal";
import { Loader2, TrendingUp, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { useAuth } from "../lib/auth.jsx";

export default function Dashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoreStock, setScoreStock] = useState(null);
  const [insightStock, setInsightStock] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get("/stocks/top12");
      setItems(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar as recomendações.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggleFav = async (stock) => {
    try {
      const r = await apiClient.post("/favorites/toggle", { stock_id: stock.id });
      setItems((prev) =>
        prev.map((s) => (s.id === stock.id ? { ...s, is_favorite: r.data.is_favorite } : s)),
      );
      toast.success(r.data.is_favorite ? "Adicionado aos favoritos" : "Removido dos favoritos");
    } catch {
      toast.error("Não foi possível atualizar o favorito.");
    }
  };

  const handleRefresh = async () => {
    if (user?.role !== "admin") return;
    setRefreshing(true);
    try {
      await apiClient.post("/admin/data/refresh");
      toast.success("Dados recalculados.");
      await load();
    } catch {
      toast.error("Falha no recálculo.");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="px-6 md:px-12 py-8 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">
            Espaço 1 · Top recomendações
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-none">
            12 Compras com maior score
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Ações dos mercados B3, NYSE e NASDAQ com maior score técnico composto hoje.
            Clique no score para auditar as 5 janelas. Cabeçalho exibe câmbio em tempo real.
          </p>
          {items.length > 0 && (
            <div
              className="mt-3 inline-flex items-center gap-2 text-xs bg-muted/50 border border-border rounded-md px-3 py-1.5 font-mono-tab"
              data-testid="dashboard-latest-quote-date"
            >
              <span className="text-muted-foreground uppercase tracking-[0.18em] text-[10px]">
                Última cotação:
              </span>
              <span className="font-semibold">
                {fmtDate(
                  items.reduce((max, it) => {
                    const d = it.quote_date || (it.captured_at ? String(it.captured_at).slice(0, 10) : "");
                    return d > max ? d : max;
                  }, items[0].quote_date || (items[0].captured_at ? String(items[0].captured_at).slice(0, 10) : ""))
                )}
              </span>
            </div>
          )}
        </div>
        {user?.role === "admin" && (
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="dashboard-refresh-button"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Recalcular
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-12">
          <Loader2 className="w-4 h-4 animate-spin" /> Carregando recomendações…
        </div>
      ) : items.length === 0 ? (
        <div
          className="rounded-md border-dashed border-2 border-border p-12 text-center"
          data-testid="dashboard-empty"
        >
          <TrendingUp className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">
            Aguardando o motor de recomendação. Os dados são populados na inicialização.
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 fade-up"
          data-testid="top12-grid"
        >
          {items.map((s) => (
            <StockCard
              key={s.id}
              stock={s}
              onToggleFav={handleToggleFav}
              onOpenScore={(stk) => setScoreStock(stk)}
              onOpenInsight={(stk) => setInsightStock(stk)}
            />
          ))}
        </div>
      )}

      <ScoreBreakdownModal
        open={!!scoreStock}
        stock={scoreStock}
        onClose={() => setScoreStock(null)}
      />
      <InsightModal
        open={!!insightStock}
        stock={insightStock}
        onClose={() => setInsightStock(null)}
      />
    </div>
  );
}
